import type { SinkConfig, SinkId, SourceId } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import type React from 'react'
import type { StackedParams } from './StackedBarSinkNext.js'
import MapSink from './MapSink.js'
import AreaChartSink from './AreaChartSink.js'
import LineChartSink from './LineChartSink.js'
import PieChartSink from './PieChartSink.js'
import TreemapSink from './TreemapSink.js'
import RankedBarSink from './RankedBarSink.js'
import {
  BiodiversityAbsoluteEffectiveSink,
  BiodiversityGiniSimpsonSink,
  BiodiversityRichnessSink,
  BiodiversityShannonSink
} from './BiodiversitySinks.js'
import { BetaDiversitySink } from './BetaDiversitySink.tsx'
import ColorLegendSink from './ColorLegendSink.tsx'
import OverlapHeatmapSink from './OverlapHeatmapSink.tsx'
import UpSetSink from './UpSetSink.tsx'
import SharedUniqueSink from './SharedUniqueSink.tsx'
import StackedBarSinkNext from './StackedBarSinkNext.tsx'

export type SinkRenderMode = 'single-bucket' | 'multi-bucket'

export type SinkCapabilities = {
  supportsKPI?: boolean
}

export type RegistryEntry = {
  component: React.ComponentType<{ sourceId: SourceId; sinkId: SinkId; config: SinkConfig; bucketKey?: string }>
  makeConfig: (id: SinkId) => SinkConfig
  renderMode: SinkRenderMode
  defaults?: { w: number; h: number; minW?: number; minH?: number; maxH?: number; maxW?: number }
  capabilities: SinkCapabilities
}
export type SinkTypeKey =
  | 'stackedBar'
  | 'rankedBar'
  | 'areaChart'
  | 'lineChart'
  | 'map'
  | 'pieChart'
  | 'treemap'
  | 'biodiversityRichness'
  | 'biodiversityShannon'
  | 'biodiversityGiniSimpson'
  | 'biodiversityAbsoluteEffective'
  | 'betaDiversity'
  | 'colorLegend'
  | 'overlapHeatmap'
  | 'upset'
  | 'sharedUnique'

export const SINK_REGISTRY: Record<SinkTypeKey, RegistryEntry> = {
  pieChart: {
    component: PieChartSink,
    makeConfig: (id) => ({
      id,
      kind: 'pieChart',
      title: 'Pie Chart',
      params: {}
    }),
    renderMode: 'single-bucket',
    defaults: { w: 4, h: 16, minW: 3, minH: 8 },
    capabilities: { supportsKPI: true }
  },
  treemap: {
    component: TreemapSink,
    makeConfig: (id) => ({
      id,
      kind: 'treemap',
      title: 'Treemap',
      params: {}
    }),
    renderMode: 'single-bucket',
    defaults: { w: 4, h: 16, minW: 3, minH: 8 },
    capabilities: { supportsKPI: true }
  },
  rankedBar: {
    component: RankedBarSink,
    makeConfig: (id) => ({
      id,
      kind: 'rankedBar',
      title: 'Ranked Bar (Top Taxa)',
      params: { topN: 10 }
    }),
    renderMode: 'single-bucket',
    defaults: { w: 4, h: 16, minW: 3, minH: 8 },
    capabilities: { supportsKPI: true }
  },
  colorLegend: {
    component: ColorLegendSink,
    makeConfig: (id) => ({
      id,
      kind: 'colorLegend',
      title: 'Color Legend',
      params: {}
    }),
    defaults: { w: 1, h: 18 },
    renderMode: 'multi-bucket',
    capabilities: {
      supportsKPI: true
    }
  },
  betaDiversity: {
    component: BetaDiversitySink,
    makeConfig: (id) => ({
      id,
      kind: 'betaDiversity',
      title: 'Beta Diversity',
      params: {}
    }),
    renderMode: 'multi-bucket',
    capabilities: {}
  },
  stackedBar: {
    component: StackedBarSinkNext,
    makeConfig: (id): SinkConfig<StackedParams> => ({
      id,
      kind: 'stackedBar',
      title: 'Stacked Chart',
      params: {
        normalize: false,
        xAxisLabel: 'Sample / Group',
        xTickMaxChars: 20,
        xTickRotate: 50
      }
    }),
    renderMode: 'multi-bucket',
    defaults: { w: 6, h: 18, minW: 3, minH: 7 },
    capabilities: {
      supportsKPI: true
    }
  },
  map: {
    component: MapSink,
    makeConfig: (id) => ({
      id,
      kind: 'map',
      title: 'Map',
      params: {}
    }),
    renderMode: 'multi-bucket',
    capabilities: {}
  },
  areaChart: {
    component: AreaChartSink,
    makeConfig: (id) => ({
      id,
      kind: 'areaChart',
      title: 'Area Chart',
      params: {}
    }),
    renderMode: 'multi-bucket',
    capabilities: { supportsKPI: true }
  },
  lineChart: {
    component: LineChartSink,
    makeConfig: (id) => ({
      id,
      kind: 'lineChart',
      title: 'Line Chart',
      params: {}
    }),
    renderMode: 'multi-bucket',
    capabilities: { supportsKPI: true }
  },
  biodiversityRichness: {
    component: BiodiversityRichnessSink,
    makeConfig: (id) => ({ id, kind: 'biodiversityRichness', title: 'Biodiversity · Species Richness', params: {} }),
    renderMode: 'multi-bucket',
    capabilities: {}
  },
  biodiversityShannon: {
    component: BiodiversityShannonSink,
    makeConfig: (id) => ({ id, kind: 'biodiversityShannon', title: 'Biodiversity · Shannon Index', params: {} }),
    renderMode: 'multi-bucket',
    capabilities: {}
  },
  biodiversityGiniSimpson: {
    component: BiodiversityGiniSimpsonSink,
    makeConfig: (id) => ({ id, kind: 'biodiversityGiniSimpson', title: 'Biodiversity · Gini-Simpson', params: {} }),
    renderMode: 'multi-bucket',
    capabilities: {}
  },
  biodiversityAbsoluteEffective: {
    component: BiodiversityAbsoluteEffectiveSink,
    makeConfig: (id) => ({ id, kind: 'biodiversityAbsoluteEffective', title: 'Biodiversity · Absolute Effective', params: {} }),
    renderMode: 'multi-bucket',
    capabilities: {}
  },
  overlapHeatmap: {
    component: OverlapHeatmapSink,
    makeConfig: (id) => ({
      id,
      kind: 'overlapHeatmap',
      title: 'Overlap Heatmap',
      params: {
        metric: 'weightedJaccard', // or 'jaccard'
        maxBuckets: 20,
        minMagnitude: 0
      }
    }),
    renderMode: 'multi-bucket',
    defaults: { w: 6, h: 16, minW: 4, minH: 10 },
    capabilities: {}
  },

  upset: {
    component: UpSetSink,
    makeConfig: (id) => ({
      id,
      kind: 'upset',
      title: 'UpSet (Intersections)',
      params: {
        maxBuckets: 8,
        topK: 12,
        minMagnitude: 0
      }
    }),
    renderMode: 'multi-bucket',
    defaults: { w: 6, h: 16, minW: 4, minH: 10 },
    capabilities: {}
  },

  sharedUnique: {
    component: SharedUniqueSink,
    makeConfig: (id) => ({
      id,
      kind: 'sharedUnique',
      title: 'Shared vs Unique',
      params: {
        maxBuckets: 30,
        minMagnitude: 0
      }
    }),
    renderMode: 'multi-bucket',
    defaults: { w: 5, h: 14, minW: 3, minH: 8 },
    capabilities: {}
  }
} as const
